import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, Observable, Subscription, tap, throwError } from 'rxjs';
import uuid from 'uuid-random';

import {
    addAutocompleterId,
    resetAutocompleter,
} from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.actions';
import { AUTOCOMPLETER_STATE_SLICE_KEY } from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.reducer';

import { TAB_ACTIVE_USER_CHANNEL } from 'Angular/features/components/navigation/constants';
import { PRO_ADMIN_TIER } from 'Angular/features/components/navigation/top-bar-nav/constants';
import { ImpersonationActiveUser, ImpersonationMenuUser } from 'AccountsApp/ts/interfaces/impersonation.interface';
import { AutocompleteStore } from 'SearchApp/ts/features/base-autocomplete/state/interfaces/autocomplete-store.interface';

import { ImpersonationService } from 'AccountsApp/ts/services/impersonation.service';

import { RefreshRequiredModalComponent } from 'Angular/features/components/modals/refresh-required-modal/refresh-required-modal.component';
import { abbreviateUserDisplayName } from 'Angular/features/components/navigation/utils';

/**
 * Impersonation Menu Component (in the nav bar)
 *
 * Responsible for:
 * - toggling impersonation on and off
 * - selecting current target user
 * - managing the impersonation target search autocompleter.
 */
@Component({
    standalone: false,
    selector: 'ycn-impersonation-menu',
    templateUrl: './impersonation-menu.template.html',
})
export class ImpersonationMenuComponent implements OnChanges, OnDestroy {
    @ViewChild('refreshRequiredModal') refreshRequiredModal!: RefreshRequiredModalComponent;

    @Input() userMenuActive: boolean = false;

    @Output() addErrorMessageHandler: EventEmitter<string> = new EventEmitter<string>();

    DEFAULT_ERROR_MESSAGE: string = 'An unknown error occurred. Please try again or contact support.';

    loading: boolean = false;

    expanded: boolean = false;

    targetUserMenuExpanded: boolean = false;

    targetUsers: ImpersonationMenuUser[] | null = null;

    currentTargetUser: ImpersonationMenuUser | null = null;

    workAsActive: boolean = false;

    autocompleterId: string;

    broadcastChannel: BroadcastChannel = new BroadcastChannel(TAB_ACTIVE_USER_CHANNEL);

    private storeSubscription: Subscription;

    constructor(
        private impersonationService: ImpersonationService,
        private readonly store: Store<{ autocompleter: AutocompleteStore }>
    ) {
        this.workAsActive = window.USER.impersonationState.userImpersonating;
        // expand Impersonation menu if active impersonation or user is on PRO_ADMIN tier (impersonation only)
        if (this.workAsActive || window.USER.tierInternalName === PRO_ADMIN_TIER) {
            this.toggleExpanded();
        }

        // Listen for changes to the active user in other tabs.
        this.broadcastChannel.addEventListener('message', (event) => {
            if (event.data !== window.USER.email) {
                // Open the modal if the new active user is different from the current user
                this.refreshRequiredModal.openModal();
            } else {
                // Close the modal if the active user is different from the current user
                this.refreshRequiredModal.closeModal();
            }
        });

        // initialize autocompleterId
        this.autocompleterId = uuid();
        this.store.dispatch(addAutocompleterId({ autocompleterId: this.autocompleterId }));
        this.storeSubscription = this.store.select(AUTOCOMPLETER_STATE_SLICE_KEY).subscribe(this.mapStateToComponent);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.userMenuActive && changes.userMenuActive.currentValue === false) {
            this.targetUserMenuExpanded = false;
        }
    }

    ngOnDestroy(): void {
        this.storeSubscription.unsubscribe();
        this.broadcastChannel.close();
    }

    mapStateToComponent = (state: AutocompleteStore): void => {
        const { selectedResult } = state[this.autocompleterId];
        if (selectedResult) {
            this.resetAutocompleter();
        }
    };

    resetAutocompleter(): void {
        this.store.dispatch(resetAutocompleter({ autocompleterId: this.autocompleterId }));
    }

    toggleExpanded(): void {
        // On the first expansion, fetch the target users for the menu. We do this on first
        // expansion rather than on component initialization to avoid making this call on every page.
        if (this.targetUsers === null) {
            this.loading = true;
            this.impersonationService.getImpersonationMenu().subscribe((response: ImpersonationMenuUser[]) => {
                this.targetUsers = response;
                this.currentTargetUser = response.length > 0 ? response[0] : null;
                this.loading = false;
            });
        }

        this.expanded = !this.expanded;
    }

    toggleWorkAs(toggleEvent: Event): void {
        // set loading immediately to prevent multiple clicks
        this.loading = true;

        const input = toggleEvent.target as HTMLInputElement;
        this.workAsActive = input.checked;

        if (!this.workAsActive) {
            // Turning OFF - end impersonation
            this.impersonationService.endImpersonation().subscribe({
                next: (response: HttpResponse<ImpersonationActiveUser>) => {
                    // Successfully ended impersonation
                    this.broadcastChannel.postMessage(response.body!.active_user_email);
                    window.location.reload();
                },
                error: () => {
                    this.loading = false;
                    // If an error occurs while ending impersonation, logout to flush the session
                    window.location.href = `/logout?next=${window.location.pathname}`;
                },
            });
        } else {
            // Turning ON - start impersonation
            if (this.currentTargetUser === null) {
                this.addErrorMessageHandler.emit('No user selected for impersonation.');
                this.workAsActive = false;
                input.checked = false;
                this.loading = false;
                return;
            }
            this.impersonateCurrentTargetUser().subscribe({
                next: () => {},
                error: () => {
                    this.workAsActive = false;
                    input.checked = false;
                },
            });
        }
    }

    setTargetUserMenuExpanded(expanded: boolean): void {
        this.targetUserMenuExpanded = expanded;
    }

    setCurrentTargetUser(user: ImpersonationMenuUser): void {
        if (this.currentTargetUser && this.currentTargetUser.email === user.email) return;

        // set as current target user
        const previousTargetUser = this.currentTargetUser;
        this.currentTargetUser = user;

        // add user to targetUsers if not already present
        const targetUsers = this.targetUsers || [];
        if (!targetUsers.some((u) => u.email === this.currentTargetUser!.email)) {
            this.targetUsers = [this.currentTargetUser, ...targetUsers];
        }

        // if currently impersonating, setting a new target user should trigger request to impersonate new user
        if (this.workAsActive) {
            this.impersonateCurrentTargetUser().subscribe({
                next: () => {},
                error: () => {
                    this.currentTargetUser = previousTargetUser;
                },
            });
        }
    }

    protected getUserDisplayName(user: ImpersonationMenuUser | null): string {
        if (user === null) {
            return '--';
        }
        return abbreviateUserDisplayName(user.first_name, user.last_name, 15);
    }

    private impersonateCurrentTargetUser(): Observable<HttpResponse<ImpersonationActiveUser>> {
        this.loading = true;
        return this.impersonationService.impersonateUser(this.currentTargetUser!.email).pipe(
            tap((response: HttpResponse<ImpersonationActiveUser>) => {
                // Successfully started impersonation
                this.broadcastChannel.postMessage(response.body!.active_user_email);
                window.location.reload();
            }),
            catchError((response: HttpErrorResponse) => {
                this.loading = false;
                this.addErrorMessageHandler.emit(response.error.error || this.DEFAULT_ERROR_MESSAGE);
                return throwError(() => response);
            })
        );
    }
}
